/**
 * Paste/drop file uploads in the editor. Inserts an "Uploading…" placeholder
 * immediately, then swaps it for the final markdown when the upload lands.
 * Cloud-only by design (signed-out users get a sign-in nudge).
 */
import { EditorView } from "@codemirror/view";
import type { Extension } from "@codemirror/state";
import { useAuth } from "@/data/auth";
import { useSignInDialog } from "@/components/SignInDialog";
import { toast } from "@/ui/toast";
import { editorSpace } from "./space";

export async function uploadAttachment(
  file: File,
  opts?: { spaceId?: string; noteId?: string },
): Promise<{ url: string; name: string; mime: string }> {
  const form = new FormData();
  form.append("file", file, file.name);
  const query = new URLSearchParams();
  if (opts?.spaceId) query.set("spaceId", opts.spaceId);
  if (opts?.noteId) query.set("noteId", opts.noteId);
  const url = `/api/attachments${query.size ? `?${query}` : ""}`;
  const res = await fetch(url, { method: "POST", body: form });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `upload failed (${res.status})`);
  }
  return (await res.json()) as { url: string; name: string; mime: string };
}

export function handleFiles(view: EditorView, files: FileList | File[], pos: number): boolean {
  const list = [...files];
  if (list.length === 0) return false;

  if (useAuth.getState().status !== "signedIn") {
    toast.info("Sign in to attach files. Attachments live in your cloud workspace.", {
      label: "Sign in",
      onClick: () => useSignInDialog.getState().setOpen(true),
    });
    return true; // swallow the drop; don't dump binary into the note
  }

  // shared notes upload into their folder's space so every member can read it
  const space = view.state.facet(editorSpace);
  const spaceId = space?.shared ? space.spaceId : undefined;

  for (const file of list) {
    const placeholder = `⟨uploading ${file.name}…⟩`;
    view.dispatch({ changes: { from: pos, insert: placeholder } });

    void uploadAttachment(file, { ...(spaceId ? { spaceId } : {}), ...(space?.noteId ? { noteId: space.noteId } : {}) })
      .then(({ url, name, mime }) => {
        const md = mime.startsWith("image/") ? `![${name}](${url})` : `[${name}](${url})`;
        replacePlaceholder(view, placeholder, md);
      })
      .catch((err: Error) => {
        replacePlaceholder(view, placeholder, "");
        toast.error(`Upload failed: ${err.message}`);
      });
  }
  return true;
}

function replacePlaceholder(view: EditorView, placeholder: string, replacement: string) {
  const text = view.state.doc.toString();
  const idx = text.indexOf(placeholder);
  if (idx === -1) return;
  view.dispatch({ changes: { from: idx, to: idx + placeholder.length, insert: replacement } });
}

export const attachmentUploads: Extension = EditorView.domEventHandlers({
  paste(event, view) {
    const files = event.clipboardData?.files;
    if (files && files.length > 0) {
      event.preventDefault();
      return handleFiles(view, files, view.state.selection.main.head);
    }
    return false;
  },
  drop(event, view) {
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      event.preventDefault();
      const pos = view.posAtCoords({ x: event.clientX, y: event.clientY }) ?? view.state.selection.main.head;
      return handleFiles(view, files, pos);
    }
    return false;
  },
});
